import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Button, Card, Field, ImageUploadField, SegmentedSelect, useBrand, apiErrorMessage } from '@aralere/shared-ui';
import type { DuplicateClientMatch, InviteClientRequest, MatchedUser, SharedEmailMatch, TenancyModel, UserLookupResponse } from '@aralere/shared-types';

/** Registrable domain of a portal hostname ("portal.alereit.com" → "alereit.com"). Mirrors the API. */
function registrableDomain(host: string): string {
  const parts = host.split('.');
  return parts.length >= 2 ? parts.slice(-2).join('.') : host;
}

type FormState = {
  clientType: 'business' | 'consumer';
  /** The business/brand this client belongs to (primaryBusinessId). */
  businessId: string;
  /** 'new' = create a new client; 'existing' = add this person to an existing client (04 #15). */
  mode: 'new' | 'existing';
  /** Existing-mode: the client to add the user to. */
  targetClientId: string;
  /** Existing-mode: the added user's role on that client. */
  roleInClient: 'billing-contact' | 'user';
  /** New-mode business: the COMPANY name. New-mode consumer / existing-mode: the person's name. */
  displayName: string;
  /** New-mode business only: the primary contact's person name. */
  contactName: string;
  email: string;
  legalName: string;
  primaryDomain: string;
  homeTenantId: string;
  /** member = account in your tenant; b2b-guest = their own work account. Consumers are always member. */
  tenancyModel: TenancyModel;
  memberUsername: string;
  /** Verified tenant domain for the member UPN (e.g. 'aralere.com'). Defaults to this portal's domain. */
  memberDomain: string;
  welcomeMessage: string;
};

const empty: FormState = {
  clientType: 'business',
  businessId: '',
  mode: 'new',
  targetClientId: '',
  roleInClient: 'user',
  displayName: '',
  contactName: '',
  email: '',
  legalName: '',
  primaryDomain: '',
  homeTenantId: '',
  tenancyModel: 'b2b-guest',
  memberUsername: '',
  memberDomain: '',
  welcomeMessage: '',
};

/** Local-part of a member username, derived from the display name ("John Smith" → "john.smith"). */
function deriveUsername(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join('.');
}

/** Read-only labelled value — used to show a resolved identity that must not be edited. */
function ReadOnlyRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium text-[var(--color-muted)]">{label}</p>
      <p className="text-sm rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.05)] px-3 py-2 mt-1 break-all">{value || '-'}</p>
    </div>
  );
}

export function AdminInvite(): JSX.Element {
  const nav = useNavigate();
  const { brand } = useBrand();
  const [form, setForm] = useState<FormState>(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [companyEdited, setCompanyEdited] = useState(false);
  // Existing-mode identity lookup (slice 3): resolve a person against Entra, then auto-fill.
  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState<UserLookupResponse | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [domainTouched, setDomainTouched] = useState(false);
  const [domainOptions, setDomainOptions] = useState<string[]>([]);
  const [businessOptions, setBusinessOptions] = useState<{ id: string; name: string; domain: string }[]>([]);
  const [existingClients, setExistingClients] = useState<{ id: string; name: string; businessId: string }[]>([]);
  // Optional company logo (04 #12) — businesses only. The client doesn't exist until after invite,
  // so we hold the File + a local preview and upload it in a second step keyed to the new clientId.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // A pre-existing person matched by login identity (spec §3.4) — drives the confirm panel below.
  const [pendingMatch, setPendingMatch] = useState<MatchedUser | null>(null);
  // A new member whose contact email already belongs to other user(s) (08 Q20) — drives the
  // shared-email override panel; the operator must tick the risk checkbox to create anyway.
  const [pendingSharedEmail, setPendingSharedEmail] = useState<SharedEmailMatch[] | null>(null);
  const [sharedEmailAck, setSharedEmailAck] = useState(false);
  // A new client whose name/domain collides with an existing SAME-BRAND client (Q21) — drives the
  // duplicate-confirm panel; the operator ticks the checkbox to create a separate client anyway.
  const [pendingDuplicate, setPendingDuplicate] = useState<DuplicateClientMatch | null>(null);
  const [duplicateAck, setDuplicateAck] = useState(false);
  // The s33 identity-mismatch guard fired (add-to-business, existing person, entered email != matched):
  // holds the backend's fact message; the confirm panel below lets the admin link that person anyway (s34).
  const [pendingIdentityMismatch, setPendingIdentityMismatch] = useState<string | null>(null);

  const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const resetLookup = () => {
    setLookupValue('');
    setLookupResult(null);
    setLookupError(null);
  };

  const resetForm = () => {
    setForm(empty);
    setUsernameEdited(false);
    setCompanyEdited(false);
    resetLookup();
    setDomainTouched(false);
    setPendingMatch(null);
    setPendingSharedEmail(null);
    setSharedEmailAck(false);
    setPendingDuplicate(null);
    setDuplicateAck(false);
    setPendingIdentityMismatch(null);
    setLogoPreview((p) => {
      if (p) URL.revokeObjectURL(p);
      return null;
    });
    setLogoFile(null);
  };

  // Resolve the typed handle against Entra (member → username/UPN, guest → email) and auto-fill.
  const doLookup = async () => {
    const identifier = lookupValue.trim();
    if (!identifier) {
      setLookupError(`Enter a ${isExternal ? 'email' : 'username'} to search.`);
      return;
    }
    setLookupError(null);
    setLookupBusy(true);
    setLookupResult(null);
    try {
      const res = await api.admin.lookupClientUser({
        identifier,
        tenancyModel: form.tenancyModel,
        // Add-user always targets one specific client; block only if they're already on THAT client.
        // (Not the brand — a person may legitimately be a contact for several clients of one brand.)
        targetClientId: form.mode === 'existing' ? form.targetClientId || undefined : undefined,
      });
      setLookupResult(res);
      if (res.found) {
        // Pin the identity to exactly what resolved, so submit links the real account instead of
        // re-deriving a UPN from the display name (mismatch → create/duplicate → the 500 you hit).
        const patch: Partial<FormState> = { displayName: res.displayName ?? '', email: res.email ?? '' };
        if (!isExternal && res.userPrincipalName) {
          const [local, domain] = res.userPrincipalName.split('@');
          if (local) patch.memberUsername = local;
          if (domain) patch.memberDomain = domain;
        }
        update(patch);
      }
    } catch (e) {
      setLookupError((e as Error).message);
    } finally {
      setLookupBusy(false);
    }
  };

  const brandDomain = brand ? registrableDomain(brand.portalHostname) : '';
  const isExternal = form.tenancyModel === 'b2b-guest';
  // After a successful existing-mode lookup the identity is fixed — show it read-only, not editable.
  const lookupFound = form.mode === 'existing' && lookupResult?.found === true;
  // The search may resolve to a blocked outcome — gray out Add so the operator can't submit into an
  // error (administrator / already a client here / directory conflict). Decided at search, not submit.
  const lookupBlocked =
    form.mode === 'existing' &&
    lookupResult?.found === true &&
    (lookupResult.outcome === 'blocked-admin' ||
      lookupResult.outcome === 'already-on-client' ||
      lookupResult.outcome === 'directory-conflict' ||
      lookupResult.outcome === 'type-mismatch');

  // Domain options = each business's verified domain (Option C; Settings will manage this later).
  useEffect(() => {
    let cancelled = false;
    api.admin
      .listBusinesses()
      .then((r) => {
        if (cancelled) return;
        const domains = [...new Set(r.businesses.map((b) => registrableDomain(b.portalHostname)))].sort();
        setDomainOptions(domains);
        setBusinessOptions(
          r.businesses
            .map((b) => ({ id: b.id, name: b.name, domain: registrableDomain(b.portalHostname) }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch(() => {
        /* fall back to the inviting brand's domain only (handled by `domains` below) */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Existing business clients for the "add to existing" picker (04 #15).
  useEffect(() => {
    api.admin
      .listClients()
      .then((r) =>
        setExistingClients(
          r.clients
            .filter((c) => c.client.clientType === 'business')
            .map((c) => ({ id: c.client.id, name: c.client.displayName, businessId: c.client.primaryBusinessId }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => {});
  }, []);

  // The brand that owns the client drives the member-UPN domain default. In existing-client mode that
  // brand comes from the chosen client; otherwise from the business selector.
  const selectedBusinessId =
    form.mode === 'existing'
      ? existingClients.find((c) => c.id === form.targetClientId)?.businessId
      : form.businessId;
  const selectedDomain = businessOptions.find((b) => b.id === selectedBusinessId)?.domain || brandDomain;

  // Default the chosen business to whichever portal you're on, until you change it.
  useEffect(() => {
    if (!brand?.id) return;
    setForm((f) => (f.businessId ? f : { ...f, businessId: brand.id }));
  }, [brand]);

  // Default the member domain to the selected business's domain (until the admin changes it).
  useEffect(() => {
    if (!selectedDomain || domainTouched) return;
    setForm((f) => (f.memberDomain === selectedDomain ? f : { ...f, memberDomain: selectedDomain }));
  }, [selectedDomain, domainTouched]);

  // Always offer the inviting brand's domain even if the businesses fetch fails.
  const domains = domainOptions.length ? domainOptions : brandDomain ? [brandDomain] : [];

  // Consumers are always internal members; businesses default to an external B2B guest.
  const onClientTypeChange = (next: FormState['clientType']) => {
    update({ clientType: next, tenancyModel: next === 'consumer' ? 'member' : 'b2b-guest' });
  };

  const submit = async (e?: FormEvent, opts?: { confirmExisting?: boolean; overrideSharedEmail?: boolean; confirmDuplicate?: boolean; confirmIdentityMatch?: boolean }) => {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    // A fresh submit clears any stale confirmation; a confirm/override re-submit keeps the panel
    // until the action lands.
    if (!opts?.confirmExisting && !opts?.overrideSharedEmail && !opts?.confirmDuplicate && !opts?.confirmIdentityMatch) {
      setPendingMatch(null);
      setPendingSharedEmail(null);
      setSharedEmailAck(false);
      setPendingDuplicate(null);
      setDuplicateAck(false);
      setPendingIdentityMismatch(null);
    }
    try {
      const isGuest = form.tenancyModel === 'b2b-guest';
      // If the operator already searched and saw "Existing user will be linked", that IS the
      // confirmation — link straight through instead of popping the redundant confirm panel. The
      // backend's needs-confirmation still protects the no-lookup path (typing details blind).
      const confirmExisting = opts?.confirmExisting || lookupResult?.outcome === 'will-link' || undefined;
      const overrideSharedEmail = opts?.overrideSharedEmail || undefined;
      const confirmDuplicate = opts?.confirmDuplicate || undefined;
      const confirmIdentityMatch = opts?.confirmIdentityMatch || undefined;

      if (form.mode === 'existing') {
        if (!form.targetClientId) {
          setError('Select a client to add the user to.');
          return;
        }
        const res = await api.admin.addClientUser(form.targetClientId, {
          displayName: form.displayName,
          email: form.email,
          tenancyModel: form.tenancyModel,
          roleInClient: form.roleInClient,
          memberUsername: isGuest ? undefined : form.memberUsername || undefined,
          memberDomain: isGuest ? undefined : form.memberDomain || undefined,
          homeTenantId: isGuest ? form.homeTenantId || undefined : undefined,
          welcomeMessage: form.welcomeMessage || undefined,
          confirmExisting,
          overrideSharedEmail,
          confirmIdentityMatch,
        });

        if (res.status === 'needs-confirmation' && res.matched) {
          setPendingMatch(res.matched);
          return;
        }
        if (res.status === 'needs-shared-email-override') {
          setPendingSharedEmail(res.sharedWith ?? []);
          return;
        }
        if (res.status === 'blocked-admin') {
          setError(
            `That ${res.matched?.tenancyModel === 'member' ? 'sign-in' : 'email'} belongs to an administrator account${res.matched ? ` (${res.matched.displayName})` : ''} and can't be added as a client contact.`,
          );
          return;
        }
        if (res.status === 'directory-conflict') {
          setError(
            `The sign-in ${res.matched?.signInIdentity ?? ''} already exists in your directory but isn't a portal user, please choose a different username.`,
          );
          return;
        }
        if (res.status === 'already-on-client') {
          setResult(`No change: ${res.matched?.displayName ?? 'that person'} is already on this client.`);
          return;
        }
        if (res.status === 'already-linked') {
          setResult(`No change. ${res.matched?.displayName ?? 'That person'} is already linked to this business.`);
          return;
        }
        // Linked-account statuses (business target) + the legacy create/invite/associate outcomes.
        setResult(
          res.status === 'linked'
            ? `${res.matched?.displayName ?? 'That person'} linked to this business.`
            : res.status === 'linked-new'
              ? `${res.matched?.displayName ?? 'The person'} added and linked to this business.`
              : res.status === 'association-added'
                ? `${res.matched?.displayName ?? 'Existing user'} added to the client.`
                : res.status === 'invited'
                  ? 'Guest invited and added to the client.'
                  : 'Member created and added to the client.',
        );
        resetForm();
        setTimeout(() => nav('/admin/clients'), 1500);
        return;
      }

      if (!form.businessId) {
        setError('Select a business / brand for this client.');
        return;
      }
      const body: InviteClientRequest = {
        clientType: form.clientType,
        displayName: form.displayName,
        contactName: form.clientType === 'business' ? form.contactName : undefined,
        email: form.email,
        legalName: form.legalName || form.displayName,
        primaryDomain: form.primaryDomain || undefined,
        homeTenantId: form.homeTenantId || undefined,
        tenancyModel: form.tenancyModel,
        memberUsername: isGuest ? undefined : form.memberUsername || undefined,
        memberDomain: isGuest ? undefined : form.memberDomain || undefined,
        associatedBusinesses: form.businessId ? [form.businessId] : [],
        welcomeMessage: form.welcomeMessage || undefined,
        invitedFromBusinessId: form.businessId || brand?.id || 'biz-aralere',
        confirmExisting,
        overrideSharedEmail,
        confirmDuplicate,
      };
      const res = await api.admin.invite(body);

      if (res.invitationStatus === 'needs-confirmation' && res.matched) {
        setPendingMatch(res.matched);
        return;
      }
      if (res.invitationStatus === 'needs-shared-email-override') {
        setPendingSharedEmail(res.sharedWith ?? []);
        return;
      }
      if (res.invitationStatus === 'needs-duplicate-confirm' && res.duplicateMatch) {
        // Passed the shared-email gate (if any); now surface the duplicate gate.
        setPendingSharedEmail(null);
        setSharedEmailAck(false);
        setPendingDuplicate(res.duplicateMatch);
        return;
      }
      if (res.invitationStatus === 'blocked-admin') {
        setError(
          `That ${res.matched?.tenancyModel === 'member' ? 'sign-in' : 'email'} belongs to an administrator account${res.matched ? ` (${res.matched.displayName})` : ''} and can't be set up as a client.`,
        );
        return;
      }
      if (res.invitationStatus === 'directory-conflict') {
        setError(
          `The sign-in ${res.matched?.signInIdentity ?? ''} already exists in your directory but isn't a portal user, please choose a different username.`,
        );
        return;
      }
      // created | invited | association-added → success. A new client record exists only when a
      // clientId came back; the optional logo upload is keyed to it (best-effort).
      let logoNote = '';
      if (res.clientId && form.clientType === 'business' && logoFile) {
        try {
          await api.admin.uploadClientLogo(res.clientId, logoFile);
        } catch {
          logoNote = ' (the logo could not be uploaded; add it later from the client editor.)';
        }
      }
      setResult(
        res.invitationStatus === 'association-added'
          ? `${res.matched?.displayName ?? 'Existing user'} added as a ${brand?.name ?? 'client'} client.${logoNote}`
          : `Invitation sent.${res.clientId ? ` Client ID: ${res.clientId}` : ''}${logoNote}`,
      );
      resetForm();
      setTimeout(() => nav('/admin/clients'), 1500);
    } catch (err) {
      const code = (err as { details?: { error?: { code?: string } } } | null)?.details?.error?.code;
      if (code === 'identity_mismatch') {
        setPendingIdentityMismatch(apiErrorMessage(err));
        return;
      }
      setError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="font-display text-3xl tracking-tight">Invite client</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Sends a branded invitation email and provisions their identity in Entra.
        </p>
      </header>

      <Card>
        <form onSubmit={submit} className="space-y-5">
          <Field label="What do you want to do?" required>
            <SegmentedSelect
              layout="chips"
              options={[
                { value: 'new', label: 'Invite a new client' },
                { value: 'existing', label: 'Add a user to an existing business' },
              ]}
              value={form.mode}
              onChange={(m) => {
                update({ mode: m });
                resetLookup();
              }}
            />
          </Field>

          {form.mode === 'existing' ? (
            <>
              <Field label="Existing Business" required tip="Business clients only. The user is added to this client; their brand comes from the client.">
                <select
                  value={form.targetClientId}
                  onChange={(e) => update({ targetClientId: e.target.value })}
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                >
                  <option value="">Select a client…</option>
                  {existingClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Role" required>
                <select
                  value={form.roleInClient}
                  onChange={(e) => update({ roleInClient: e.target.value as 'billing-contact' | 'user' })}
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                >
                  <option value="user">User</option>
                  <option value="billing-contact">Billing Contact</option>
                </select>
              </Field>
            </>
          ) : null}

          {form.mode === 'new' ? (
          <Field label="Client type" required>
            <SegmentedSelect
              layout="chips"
              options={[
                { value: 'business', label: 'Business' },
                { value: 'consumer', label: 'Consumer / Personal' },
              ]}
              value={form.clientType}
              onChange={(t) => onClientTypeChange(t)}
            />
          </Field>
          ) : null}

          {form.mode === 'new' ? (
          <Field label="Business / brand" required tip="Which brand this client belongs to. Drives their portal branding, invoices, and welcome email. Independent of which portal you are signed in to.">
            <select
              value={form.businessId}
              onChange={(e) => update({ businessId: e.target.value })}
              className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              {businessOptions.length === 0 ? <option value="">Loading…</option> : null}
              {businessOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          ) : null}

          {form.mode === 'existing' || form.clientType === 'business' ? (
            <Field label="Account type" required tip="Member hosts their identity in your tenant; guest (external) uses their own work account.">
              <select
                value={form.tenancyModel}
                onChange={(e) => {
                  update({ tenancyModel: e.target.value as TenancyModel });
                  resetLookup();
                }}
                className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
              >
                <option value="b2b-guest">External: B2B guest (uses their own work account)</option>
                <option value="member">Member: account in your tenant</option>
              </select>
            </Field>
          ) : null}

          {form.mode === 'existing' ? (
            <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-3">
              <p className="text-sm font-medium">Find the person</p>
              <p className="text-xs text-[var(--color-muted)]">
                {isExternal
                  ? 'Search by their email; external guests sign in with their own account.'
                  : 'Search by their sign-in username, e.g. john.smith@alereit.com.'}
              </p>
              <div className="flex gap-2">
                <input
                  value={lookupValue}
                  onChange={(e) => {
                    setLookupValue(e.target.value);
                    setLookupResult(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void doLookup();
                    }
                  }}
                  className="flex-1 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  placeholder={isExternal ? 'john@acmecorp.com' : 'john.smith@alereit.com'}
                />
                <Button type="button" variant="secondary" onClick={() => void doLookup()} disabled={lookupBusy}>
                  {lookupBusy ? 'Searching…' : 'Find'}
                </Button>
              </div>
              {lookupError ? <p className="text-sm text-[var(--tint-red-text)]">{lookupError}</p> : null}
              {lookupResult && !lookupResult.found ? (
                <p className="text-sm text-[var(--tint-warn-text)]">
                  No directory match. Check the {isExternal ? 'email' : 'username'}, or fill in the details below to
                  add them as a new user.
                </p>
              ) : null}
              {lookupResult?.found ? (
                lookupResult.outcome === 'blocked-admin' ? (
                  <p className="text-sm text-[var(--tint-red-text)]">
                    <span className="font-medium">{lookupResult.displayName}</span> is an administrator account and
                    can't be added as a client contact.
                  </p>
                ) : lookupResult.outcome === 'already-on-client' ? (
                  <p className="text-sm text-[var(--tint-warn-text)]">
                    <span className="font-medium">{lookupResult.displayName}</span> is already a client here,
                    nothing to add.
                  </p>
                ) : lookupResult.outcome === 'directory-conflict' ? (
                  <p className="text-sm text-[var(--tint-red-text)]">
                    The sign-in {lookupResult.userPrincipalName ?? ''} exists in your directory but isn't a portal
                    user, please choose a different username.
                  </p>
                ) : lookupResult.outcome === 'type-mismatch' ? (
                  <p className="text-sm text-[var(--tint-red-text)]">
                    {form.tenancyModel === 'member'
                      ? `${lookupResult.displayName} is already an External account (they sign in through their own organization). An External user can't be added as a Member. Please switch the account type to External to link them.`
                      : `${lookupResult.displayName} is already a Member account (they sign in through your tenant). A Member can't be linked as an External user. Please switch the account type to Member to link them.`}
                  </p>
                ) : (
                  <p className="text-sm text-[var(--tint-green-text)]">
                    Found <span className="font-medium">{lookupResult.displayName}</span>
                    {lookupResult.email ? ` (${lookupResult.email})` : ''}.{' '}
                    {lookupResult.inSystem
                      ? 'Existing user will be linked to this client.'
                      : `In your directory, ${lookupResult.displayName} will be added as a client.`}
                  </p>
                )
              ) : null}
              {lookupResult?.found ? (
                <div className="grid gap-3 sm:grid-cols-2 pt-1">
                  {!isExternal && lookupResult.userPrincipalName ? (
                    <ReadOnlyRow label="Username" value={lookupResult.userPrincipalName} />
                  ) : null}
                  <ReadOnlyRow label="Name" value={lookupResult.displayName ?? ''} />
                  <ReadOnlyRow label="Email" value={lookupResult.email ?? ''} />
                </div>
              ) : null}
            </div>
          ) : null}

          {form.mode === 'existing' || form.clientType === 'consumer' ? (
            lookupFound ? null : (
            <Field label="Full name" required>
              <input
                required
                value={form.displayName}
                onChange={(e) => {
                  const dn = e.target.value;
                  update(usernameEdited ? { displayName: dn } : { displayName: dn, memberUsername: deriveUsername(dn) });
                }}
                className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                placeholder="John Smith"
              />
            </Field>
            )
          ) : (
            <>
              <Field label="Company name" required tip="Shown in their portal and saved to Microsoft as the company. Fills automatically from the legal name.">
                <input
                  required
                  value={form.displayName}
                  onChange={(e) => {
                    setCompanyEdited(true);
                    update({ displayName: e.target.value });
                  }}
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  placeholder="Acme Corporation"
                />
              </Field>
              <Field label="Legal business name" tip="Used on invoices. Blank uses the company name.">
                <input
                  value={form.legalName}
                  onChange={(e) => {
                    const ln = e.target.value;
                    update(companyEdited ? { legalName: ln } : { legalName: ln, displayName: ln });
                  }}
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  placeholder="Acme Corporation, Inc."
                />
              </Field>
              <Field label="Full name" required>
                <input
                  required
                  value={form.contactName}
                  onChange={(e) => {
                    const cn = e.target.value;
                    update(usernameEdited ? { contactName: cn } : { contactName: cn, memberUsername: deriveUsername(cn) });
                  }}
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  placeholder="John Smith"
                />
                <p className="text-xs text-[var(--color-muted)] mt-1">Primary contact</p>
              </Field>
            </>
          )}

          {!lookupFound ? (
            <Field label="Email" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                placeholder={isExternal ? 'john@acmecorp.com' : 'personal@gmail.com'}
              />
              {form.mode === 'existing' ? (
                <p className="text-xs text-[var(--color-muted)] mt-1">
                  {isExternal
                    ? 'If this email already belongs to someone, they’ll be linked to this client, and no duplicate account is created.'
                    : 'Their contact email, which need not be unique. Their sign-in username is what identifies them.'}
                </p>
              ) : null}
            </Field>
          ) : null}

          {form.mode === 'existing' && isExternal && !lookupFound ? (
            <Field label="Home tenant ID (optional)" tip="Their M365 tenant ID. Used for SSO routing. Can be set later.">
              <input
                value={form.homeTenantId}
                onChange={(e) => update({ homeTenantId: e.target.value })}
                className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)] font-mono text-xs"
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Field>
          ) : null}

          {form.mode === 'new' && form.clientType === 'business' ? (
            <>
              <Field label="Primary domain">
                <input
                  value={form.primaryDomain}
                  onChange={(e) => update({ primaryDomain: e.target.value })}
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  placeholder="acmecorp.com"
                />
              </Field>
              {isExternal ? (
                <Field label="Home tenant ID (optional)" tip="Their M365 tenant ID. Used for SSO routing. Can be set later.">
                  <input
                    value={form.homeTenantId}
                    onChange={(e) => update({ homeTenantId: e.target.value })}
                    className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)] font-mono text-xs"
                    placeholder="00000000-0000-0000-0000-000000000000"
                  />
                </Field>
              ) : null}
            </>
          ) : null}

          {form.mode === 'new' && form.clientType === 'business' ? (
            <ImageUploadField
              label="Company logo (optional)"
              hint="JPG, PNG, or WebP up to 5 MB. Uploaded right after the client is created."
              shape="square"
              size={72}
              // 02 s115: the admin displays the COMPANY name. This feeds AvatarImage's
              // fallback initial, so legal-name-first showed the wrong letter once the
              // two names diverged. legalName stays only as a fallback for a cleared field.
              name={form.displayName || form.legalName || 'Logo'}
              hasImage={!!logoFile}
              src={logoPreview}
              onSelect={(file) => {
                if (logoPreview) URL.revokeObjectURL(logoPreview);
                setLogoFile(file);
                setLogoPreview(URL.createObjectURL(file));
              }}
              onRemove={() => {
                if (logoPreview) URL.revokeObjectURL(logoPreview);
                setLogoFile(null);
                setLogoPreview(null);
              }}
            />
          ) : null}

          {!isExternal && !lookupFound ? (
            <Field label="Username">
              <div className="flex items-stretch gap-2">
                <input
                  value={form.memberUsername}
                  onChange={(e) => {
                    setUsernameEdited(true);
                    update({ memberUsername: e.target.value });
                  }}
                  className="flex-1 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  placeholder="john.smith"
                />
                <span className="self-center text-sm text-[var(--color-muted)]">@</span>
                <select
                  aria-label="Account domain"
                  value={form.memberDomain}
                  onChange={(e) => {
                    setDomainTouched(true);
                    update({ memberDomain: e.target.value });
                  }}
                  className="w-44 shrink-0 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                >
                  {domains.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-[var(--color-muted)] mt-1">
                Leave blank to auto-generate from the display name. Creates{' '}
                {form.memberUsername || 'firstname.lastname'}@{form.memberDomain || brandDomain}.
              </p>
            </Field>
          ) : null}

          <Field label="Welcome message (optional)">
            <textarea
              value={form.welcomeMessage}
              onChange={(e) => update({ welcomeMessage: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
              placeholder="Thanks for choosing us, looking forward to working together."
            />
          </Field>

          {error ? (
            <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)]">
              {error}
            </div>
          ) : null}
          {result ? (
            <div className="rounded-md bg-[var(--tint-green-bg-soft)] border border-[var(--tint-green-border)] px-4 py-3 text-sm text-[var(--tint-green-text)]">
              {result}
            </div>
          ) : null}

          {pendingMatch ? (
            <div className="rounded-md bg-[var(--tint-warn-bg-soft)] border border-[var(--tint-warn-border)] px-4 py-3 text-sm text-[var(--tint-warn-text)] space-y-2">
              <p className="font-medium">
                This {pendingMatch.tenancyModel === 'member' ? 'sign-in' : 'email'} already belongs to{' '}
                {pendingMatch.displayName}.
              </p>
              <p>
                {form.mode === 'existing'
                  ? `They're an existing ${pendingMatch.tenancyModel === 'member' ? 'member' : 'guest'}. Adding them links their existing account to this client. No duplicate account is created.`
                  : `They're an existing ${pendingMatch.tenancyModel === 'member' ? 'member' : 'guest'}. Continuing gives them a new ${brand?.name ?? 'client'} client record for this business. No duplicate account is created.`}
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  loading={submitting}
                  disabled={submitting}
                  onClick={() => void submit(undefined, { confirmExisting: true })}
                >
                  {form.mode === 'existing' ? 'Add them to this client' : 'Create the client with them'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setPendingMatch(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {pendingIdentityMismatch ? (
            <div className="rounded-md bg-[var(--tint-warn-bg-soft)] border border-[var(--tint-warn-border)] px-4 py-3 text-sm text-[var(--tint-warn-text)] space-y-2">
              <p className="font-medium">Is this the same person?</p>
              <p>{pendingIdentityMismatch}</p>
              <p>
                You entered {form.email}. If this is the same person, we'll link their existing account and
                keep their current email. If it's a different person, cancel and give them a different
                sign-in name.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  loading={submitting}
                  disabled={submitting}
                  onClick={() => void submit(undefined, { confirmIdentityMatch: true })}
                >
                  Link this person
                </Button>
                <Button type="button" variant="ghost" onClick={() => setPendingIdentityMismatch(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {pendingSharedEmail ? (
            <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)] space-y-2">
              <p className="font-medium">
                ⚠️ This contact email is already in use by {pendingSharedEmail.length} other account
                {pendingSharedEmail.length === 1 ? '' : 's'}:{' '}
                {pendingSharedEmail
                  .map((s) => `${s.displayName} (${s.tenancyModel === 'member' ? 'member' : 'guest'})`)
                  .join(', ')}
                .
              </p>
              <p>
                Sharing a contact email means password-reset links, security alerts, account-recovery
                messages, and account activity for all of these accounts land in the same inbox. Anyone
                with access to it can take over any of them. Only proceed for a genuinely shared, trusted
                mailbox (e.g. a family or a small team that already shares this address).
              </p>
              <label className="flex items-start gap-2 pt-1">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={sharedEmailAck}
                  onChange={(e) => setSharedEmailAck(e.target.checked)}
                />
                <span>I accept this security and privacy risk. I want to create a separate account on this email.</span>
              </label>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  loading={submitting}
                  disabled={submitting || !sharedEmailAck}
                  onClick={() => void submit(undefined, { overrideSharedEmail: true })}
                >
                  Create anyway
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPendingSharedEmail(null);
                    setSharedEmailAck(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {pendingDuplicate ? (
            <div className="rounded-md bg-[var(--tint-warn-bg-soft)] border border-[var(--tint-warn-border)] px-4 py-3 text-sm text-[var(--tint-warn-text)] space-y-2">
              <p className="font-medium">
                {pendingDuplicate.matchedOn === 'domain'
                  ? `A business using ${pendingDuplicate.primaryDomain} already exists under ${pendingDuplicate.brandName} (${pendingDuplicate.displayName}). Continue only if this is intentional.`
                  : pendingDuplicate.matchedOn === 'both'
                    ? `A client named "${pendingDuplicate.displayName}" using ${pendingDuplicate.primaryDomain} already exists under ${pendingDuplicate.brandName}. Continue only if this is a separate client.`
                    : `A client named "${pendingDuplicate.displayName}" already exists under ${pendingDuplicate.brandName}. Continue only if this is a separate client.`}
              </p>
              <label className="flex items-start gap-2 pt-1">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={duplicateAck}
                  onChange={(e) => setDuplicateAck(e.target.checked)}
                />
                <span>Yes, this is a different client.</span>
              </label>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  loading={submitting}
                  disabled={submitting || !duplicateAck}
                  onClick={() => void submit(undefined, { confirmDuplicate: true })}
                >
                  Create this client anyway
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setPendingDuplicate(null);
                    setDuplicateAck(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={submitting} disabled={submitting || !!pendingMatch || !!pendingSharedEmail || !!pendingDuplicate || lookupBlocked}>
              {submitting
                ? form.mode === 'existing'
                  ? 'Adding…'
                  : 'Sending…'
                : form.mode === 'existing'
                  ? 'Add user'
                  : 'Send invitation'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => nav('/admin/clients')}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

